import useTableBody from './UseTableBody';
import useTableWrapper from './UseTableWrapper';

import TableContextProvider from '../../../../state/table/TableContextProvider';

import { NO_DATA_TEXT } from '../../utils/TableConstants';
import { LOADING_MSG } from '../../../../utils/Constants';

import tableStyle from '../../Table.module.css';

/**
 * Functional component for rendering the rows of a nested table.
 * @param {Array} data array to be displayed in the nested table, i.e. person.personOrgRole
 * @param {Function} renderRow function used to render each row of the table
 * @param {Number} colSpan number of columns the no data text will span 
 */
const NestedBody = ({ data, renderRow, colSpan = 1, noDataText = NO_DATA_TEXT, className = '', style = undefined, children }) => (
  <tbody className={className} style={style}>
    {children}
    {Array.isArray(data) && data.length > 0
      ? data.map((dataObj, i) => renderRow(dataObj, i))
      : <tr><td colSpan={colSpan}>{noDataText}</td></tr>
    }
  </tbody>
);

/**
 * Functional component for rendering the rows of a table.
 * @param {Function} renderRow function used to render each row of the table
 * @param {Number} colSpan number of columns the no data/loading text will span
 */
const Body = ({ renderRow, colSpan, isLoading = false, noDataText = NO_DATA_TEXT, loadingText = LOADING_MSG,
  className = '', style = undefined, children }) => {
  const { sortedData } = useTableBody();

  return (
    <tbody className={className} style={style}>
      {children}
      {isLoading
        ? <tr><td colSpan={colSpan}>{loadingText}</td></tr>
        : Array.isArray(sortedData) && sortedData.length > 0
          ? sortedData.map((dataObj, i) => renderRow(dataObj, i))
          : <tr><td colSpan={colSpan}>{noDataText}</td></tr>
      }
    </tbody>
  );
};

/**
 * Styling component for tables that appear within other tables.
 */
const Nested = ({ className = '', style = undefined, children }) => (
  <table className={[tableStyle.Table, tableStyle.Nested, className].join(' ')} style={style}>
    {children}
  </table>
);

/**
 * Styling component for mobile tables.
 */
const Small = ({ className = '', style = undefined, children }) => (
  <table className={[tableStyle.Table, tableStyle.Small, 'visible-xs', className].join(' ')} style={style}>
    {children}
  </table>
);

/**
 * Styling component for desktop tables.
 */
const Large = ({ className = '', style = undefined, children }) => (
  <table className={[tableStyle.Table, tableStyle.Large, 'hidden-xs', className].join(' ')} style={style}>
    {children}
  </table>
);

/**
 * Wrapper component that initializes tableState but does NOT provide context. 
 * The TableContextProvider must be setup manually when using this component.
 * @param {Array} data arrayData to be displayed in the table
 * @param {String} rowIdKey unique key for each row in the table, i.e. 'personId'
 */
const Wrapper = ({ data, rowIdKey, defaultSortKey = '', defaultSortType = '', defaultSortDirection = '',
  expandAll = false, selectAll = false, children }) => {
  useTableWrapper(data, rowIdKey, defaultSortKey, defaultSortType, defaultSortDirection, expandAll, selectAll);

  return children;
};

/**
 * Wrapper component that initializes tableState and provides context to all child components.
 * @param {Array} data arrayData to be displayed in the table
 * @param {String} rowIdKey unique key for each row in the table, i.e. 'personId'
 */
const ContextWrapper = ({ data, rowIdKey, defaultSortKey = '', defaultSortType = '', defaultSortDirection = '',
  expandAll = false, selectAll = false, children }) => (
  <TableContextProvider>
    <Wrapper data={data} rowIdKey={rowIdKey} defaultSortKey={defaultSortKey} defaultSortType={defaultSortType} defaultSortDirection={defaultSortDirection} expandAll={expandAll} selectAll={selectAll}>
      {children}
    </Wrapper>
  </TableContextProvider>
);

export { ContextWrapper, Wrapper, Nested, Small, Large, Body, NestedBody };